import type { MenschMove, MenschPlayerCount, MenschView } from "@bing/engine-mensch";
import { MENSCH_DIFFICULTY_CHOICES, type BotDifficulty } from "@bing/shared";
import { useEffect, useMemo, useState, type ReactNode } from "react";
import { useStore } from "zustand";
import { Boot } from "../components/Boot";
import { Button } from "../components/Button";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { DifficultySelect } from "../components/DifficultySelect";
import { MatchEndCard, namedMember } from "../components/MatchEndCard";
import { faDigits } from "../i18n/digits";
import { StartRow, StartScreenShell } from "../components/StartScreenShell";
import { GAME_SERVER_URL } from "../config";
import { fa } from "../i18n/fa";
import {
  MenschColorSelect,
  MenschPlayersSelect,
  MenschTable,
  menschSeatName,
  type MenschIdentity,
} from "../screens/MenschTable";
import {
  getMenschColor,
  getMenschDifficulty,
  getMenschPlayers,
  setMenschColor,
  setMenschDifficulty,
  setMenschPlayers,
} from "../settings/prefs";
import { useSession } from "../store/context";
import { useOverlayActive } from "./lifecycle";
import { useOutcomeSfx } from "./game-sfx";
import { useEndReveal } from "./use-end-reveal";
import type { GameResult } from "./connection";
import { takeResumeIntent } from "./fresh-solo";
import { createColyseusGameConnector } from "./game-connection";
import { createGameStore } from "./game-store";
import { useTableChannelGate } from "./table-channel-gate";
import { useSoloLoad } from "./use-solo-load";

/** End-of-game card. Reused by the online container. */
export function MenschEndOverlay({
  view,
  result,
  self,
  players,
  onAgain,
  againDisabled,
  againHint,
  againHidden,
  onLeave,
}: {
  view: MenschView;
  result: GameResult;
  /** card-98 item 12: the players' rows — the same identities the board's strips get (seat-indexed;
   *  null for a bot or an unresolved seat). */
  self?: MenschIdentity | null;
  players?: readonly (MenschIdentity | null)[] | null;
  onAgain: () => void;
  /** ONLINE, private tables only: «بازی دوباره» is the HOST's call, so a guest sees the
   *  button inert until the host has offered one. Undefined (solo, public) ⇒ enabled. */
  againDisabled?: boolean;
  /** Why it is inert, in one Persian line. Rendered only when present. */
  againHint?: string;
  /** CONTRACT C3 — the rematch cannot work from here: NO «بازی دوباره» at all (a dead button
   *  that looks alive is worse than none); `againHint` may still say why. Undefined (solo, and
   *  wherever the button is shown) ⇒ drawn. */
  againHidden?: boolean;
  onLeave: () => void;
}): ReactNode {
  const won = result.winners.includes(view.you);
  // WHY IT ENDED, when the board did not (session B item 4): at a PRIVATE table for two the
  // opponent's «انصراف» forfeits, and this card used to greet that win with «همهٔ مهره‌هات رسیدن
  // خونه!». The same idiom as شطرنج / تخته نرد / دوز — the room's reason wins when it has one.
  const roomReason = result.reason === "resign" && won ? fa.mensch.endResign : null;
  // card-98 item 12: the shared template — the gold disc (this card's own), each PLAYER a side (as شلم
  // draws a team): the winner first, then by pieces home — منچ's own figure, «n/۴»
  // the seats the view draws (a partial frame: the result's)
  const seatCount = view.tokens?.length ?? result.seatScores.length;
  const order = Array.from({ length: seatCount }, (_u, seat) => seat).sort(
    (a, b) =>
      Number(result.winners.includes(b)) - Number(result.winners.includes(a)) ||
      (view.finished?.[b] ?? 0) - (view.finished?.[a] ?? 0) ||
      a - b,
  );
  return (
    <MatchEndCard
      label={fa.mensch.gameOver}
      outcome={won ? "won" : "lost"}
      glyph="🎲"
      title={won ? fa.mensch.youWin : fa.mensch.youLose}
      reason={roomReason ?? (won ? fa.mensch.winSubtitle : fa.mensch.loseSubtitle)}
      sides={order.map((seat) => {
        const parts = seat === view.you ? self : (players?.[seat] ?? null);
        return {
          key: `seat-${seat}`,
          mine: seat === view.you,
          mark: result.winners.includes(seat) ? "🥇" : "",
          members: [
            namedMember(
              seat,
              parts,
              menschSeatName(seat, view.you, seatCount, self, players),
              "fox",
            ),
          ],
          result: view.finished
            ? `${faDigits(view.finished[seat] ?? 0)}/${faDigits(4)}`
            : undefined,
          attrs: { "data-seat": seat },
        };
      })}
      playAgain={fa.mensch.playAgain}
      leave={fa.mensch.leave}
      onAgain={onAgain}
      againDisabled={againDisabled}
      againHint={againHint}
      againHidden={againHidden}
      onLeave={onLeave}
    />
  );
}

/**
 * منچ's own names for its two levels. NOT «متوسط»/«سخت»: those are two thirds of a three-rung
 * ladder, and reading «متوسط» with no «آسان» beside it implies a rung that does not exist.
 */
const MENSCH_DIFFICULTY_LABELS: Readonly<Record<BotDifficulty, string>> = {
  easy: fa.mensch.difficultyNormal, // unreachable via MENSCH_DIFFICULTY_CHOICES; folded to normal
  medium: fa.mensch.difficultyNormal,
  hard: fa.mensch.difficultyHard,
};

/** Pre-game START screen: pick the opponent level ONCE; it is LOCKED for the game. */
function MenschStartScreen({
  color,
  onColorChange,
  players,
  onPlayersChange,
  difficulty,
  onDifficultyChange,
  onStart,
  onLeave,
}: {
  /** ITEM 8 — the arm-colour the player picked (0..3). */
  color: number;
  onColorChange: (color: number) => void;
  players: MenschPlayerCount;
  onPlayersChange: (players: MenschPlayerCount) => void;
  /** TWO levels only — see `MENSCH_DIFFICULTY_CHOICES`. */
  difficulty: BotDifficulty;
  onDifficultyChange: (value: BotDifficulty) => void;
  onStart: () => void;
  onLeave: () => void;
}): ReactNode {
  return (
    <StartScreenShell
      title={fa.mensch.title}
      emoji="🏁"
      heading={fa.mensch.chooseColor}
      leaveLabel={fa.mensch.leave}
      startLabel={fa.mensch.startGame}
      onStart={onStart}
      onLeave={onLeave}
    >
      <MenschColorSelect value={color} onChange={onColorChange} />
      {/*
        TWO levels, not three. منچ used to offer none at all (the level was pinned server-side)
        because its "hard" tier was a relabelling of its "medium" one — the two ran the identical
        scoring function and measured EXACTLY 50.0% head to head. There are genuinely two now
        (61.4% on fresh seeds, n=500), and exactly two are shown: with 80.3% of منچ turns offering
        no choice at all, a third rung could only ever be a third name for the same opponent.
      */}
      <StartRow label={fa.mensch.difficulty}>
        <DifficultySelect
          value={difficulty}
          onChange={onDifficultyChange}
          ariaLabel={fa.mensch.difficulty}
          levels={MENSCH_DIFFICULTY_CHOICES}
          labels={MENSCH_DIFFICULTY_LABELS}
          compact
        />
      </StartRow>
      <StartRow label={fa.mensch.players}>
        <MenschPlayersSelect value={players} onChange={onPlayersChange} />
      </StartRow>
    </StartScreenShell>
  );
}

/** Owns the solo منچ connection + store for ONE game at a FIXED (locked) difficulty. */
function MenschSoloRound({
  color,
  players,
  difficulty,
  onAgain,
  onExit,
}: {
  /** ITEM 8 — locked for the game, like the table size. */
  color: number;
  /** Locked for the whole game, like the difficulty — «بازی دوباره» returns to the picker. */
  players: MenschPlayerCount;
  /** FIXED for this game — chosen on the start screen, never changes mid-game. */
  difficulty: BotDifficulty;
  onAgain: () => void;
  onExit: () => void;
}): ReactNode {
  const initData = useSession((s) => s.initDataRaw);
  const profile = useSession((s) => s.profile);
  const clearActiveGame = useSession((s) => s.clearActiveGame);
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  // Bumped by the channel gate's «عضو شدم» to re-attempt the join (a fresh store).
  const [round, setRound] = useState(0);
  const store = useMemo(
    () =>
      createGameStore(
        createColyseusGameConnector<MenschMove, MenschView>({
          wsUrl: GAME_SERVER_URL,
          room: "mensch",
          getInitData: () => initData,
          players,
          // FIXED for this game — chosen on the start screen. The server maps it through
          // `menschLevelFrom`, so منچ's two levels are what actually reach the bot.
          difficulty,
        }),
      ),
    [initData, round, players, difficulty],
  );
  useEffect(() => () => store.getState().disconnect(), [store]);

  const status = useStore(store, (s) => s.status);
  const view = useStore(store, (s) => s.view);
  const legal = useStore(store, (s) => s.legal);
  const result = useStore(store, (s) => s.result);
  const showEnd = useEndReveal(result !== null);
  useOutcomeSfx(showEnd, result?.winners, view?.you);
  const error = useStore(store, (s) => s.error);
  const overlayActive = useOverlayActive();
  // A NEW game the game-server refused for channel membership: the channel-join screen, whose
  // «عضو شدم» re-attempts the join (`round`). See `useTableChannelGate`.
  const channelGate = useTableChannelGate({
    status,
    error,
    restart: () => setRound((r) => r + 1),
    onLeave: () => {
      store.getState().disconnect();
      onExit();
    },
  });
  const send = useStore(store, (s) => s.send);
  const ready = useStore(store, (s) => s.ready);
  const { loading, progress } = useSoloLoad(view != null, ready, store);

  const doLeave = (): void => {
    store.getState().disconnect();
    onExit();
  };
  const again = (): void => {
    store.getState().disconnect();
    onAgain();
  };

  if (channelGate && !overlayActive) return channelGate;
  if (status === "error" && !overlayActive) {
    return (
      <div
        style={{
          height: "var(--app-h)",
          display: "grid",
          placeItems: "center",
          background: "var(--room-bg)",
          padding: 24,
          textAlign: "center",
        }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 16, alignItems: "center" }}>
          <p style={{ color: "var(--danger)", margin: 0, fontWeight: 700 }}>
            {error ?? fa.mensch.connectionError}
          </p>
          <Button variant="primary" onClick={onExit}>
            {fa.mensch.leave}
          </Button>
        </div>
      </div>
    );
  }
  // The connecting splash carries its own exit — never trap the player on it.
  if (loading || !view)
    return (
      <Boot
        caption={fa.mensch.connecting}
        progress={progress}
        onLeave={doLeave}
        leaveLabel={fa.mensch.leave}
      />
    );

  return (
    <>
      {/* The header's exit stays reachable beside the end card (the card starts below the band);
          once the game is over it is the card's «خروج» — there is no game left to keep or end. */}
      <MenschTable
        view={view}
        legal={legal}
        onMove={send}
        onLeave={status === "gameover" ? doLeave : () => setConfirmingLeave(true)}
        self={profile}
        myColor={color}
      />
      {result && showEnd ? (
        <MenschEndOverlay
          result={result}
          view={view}
          self={profile}
          onAgain={again}
          onLeave={doLeave}
        />
      ) : null}
      {confirmingLeave ? (
        <ConfirmDialog
          body={fa.confirm.exitChoiceBody}
          confirmLabel={fa.confirm.leaveResumable}
          danger={false}
          onConfirm={() => {
            setConfirmingLeave(false);
            doLeave();
          }}
          secondaryLabel={fa.confirm.endGame}
          onSecondary={() => {
            setConfirmingLeave(false);
            clearActiveGame("mensch");
            void store
              .getState()
              .endGame()
              .catch(() => {})
              .finally(doLeave);
          }}
          onCancel={() => setConfirmingLeave(false)}
        />
      ) : null}
    </>
  );
}

/** Solo منچ: START screen (locked level) → the game. */
export function MenschGame(): ReactNode {
  const setView = useSession((s) => s.setView);
  // ITEM 3 — an explicit «ادامه» from the lobby skips this picker. Showing it would ask for
  // settings the server is about to ignore in favour of the saved game — the same silent
  // override, one screen later. A fresh start still lands here.
  const [phase, setPhase] = useState<"select" | "playing">(() =>
    takeResumeIntent() ? "playing" : "select",
  );
  const [color, setColor] = useState<number>(getMenschColor());
  const [players, setPlayers] = useState<MenschPlayerCount>(getMenschPlayers());
  const [difficulty, setDifficulty] = useState<BotDifficulty>(getMenschDifficulty());

  if (phase === "select") {
    return (
      <MenschStartScreen
        color={color}
        onColorChange={(c) => {
          setColor(c);
          setMenschColor(c);
        }}
        players={players}
        onPlayersChange={(n) => {
          setPlayers(n);
          setMenschPlayers(n);
        }}
        difficulty={difficulty}
        onDifficultyChange={(value) => {
          setDifficulty(value);
          setMenschDifficulty(value); // remembered, like the colour and the table size
        }}
        onStart={() => setPhase("playing")}
        onLeave={() => setView("lobby")}
      />
    );
  }
  return (
    <MenschSoloRound
      color={color}
      players={players}
      difficulty={difficulty}
      onAgain={() => setPhase("select")}
      onExit={() => setView("lobby")}
    />
  );
}
